import { useSendOffer } from '@/hooks/queries/usePoolQueries';
import { useCounterOfferStore } from '@/stores/counterOfferStore';
import { OfferType, MakeCounterOfferPayload } from '@/types/offer';
import { validateCounterOfferTerms } from '@/utils/formValidation/counterOfferForm';

type UseCounterOfferSubmissionOptions = {
  poolId: number;
  onSuccess?: () => void;
  onError?: (error: string) => void;
};

/**
 * Hook for submitting counter offer with validation
 */
export const useCounterOfferSubmission = (options: UseCounterOfferSubmissionOptions) => {
  const { poolId, onSuccess, onError } = options;
  const sendOfferMutation = useSendOffer();
  const setErrors = useCounterOfferStore((state) => state.setErrors);
  const setActiveTab = useCounterOfferStore((state) => state.setActiveTab);
  const setShowSuccess = useCounterOfferStore((state) => state.setShowSuccess);

  const submitCounterOffer = async () => {
    const state = useCounterOfferStore.getState();
    const { counterAmount, counterRate, counterTermMonths, note, requiredDocuments } = state;

    // Validate counter offer terms
    const validation = validateCounterOfferTerms({
      counterAmount,
      counterRate,
      counterTermMonths,
    });

    if (!validation.isValid) {
      // Convert validation errors to store format
      const storeErrors: { amount?: string; rate?: string; term?: string } = {};
      if (validation.errors.counterAmount) {
        storeErrors.amount = validation.errors.counterAmount[0];
      }
      if (validation.errors.counterRate) {
        storeErrors.rate = validation.errors.counterRate[0];
      }
      if (validation.errors.counterTermMonths) {
        storeErrors.term = validation.errors.counterTermMonths[0];
      }

      setErrors(storeErrors);
      setActiveTab('poolTerms');

      const errorMsg = 'Please fix all form errors before submitting.';
      onError?.(errorMsg);
      throw new Error(errorMsg);
    }

    try {
      const payload: MakeCounterOfferPayload = {
        pool_id: poolId,
        type: OfferType.COUNTER_OFFER,
        counter_offer_info_schema: {
          required_documents: requiredDocuments,
          amount: counterAmount,
          roi_rate: counterRate,
          term_months: counterTermMonths,
          note: note,
        },
      };

      await sendOfferMutation.mutateAsync(payload);
      setShowSuccess(true);
      onSuccess?.();
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Network error. Please check your connection and try again.';
      onError?.(errorMsg);
      throw error;
    }
  };

  return {
    submitCounterOffer,
    isSubmitting: sendOfferMutation.isPending,
  };
};
